import { DAYS_SPAN_PERIODS, EChartPeriod, HOURS_SPAN_PERIODS, MINUTES_SPAN_PERIODS } from '@aqx-core/common/api/chart';
import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { TAppLanguageISOCountryCode, i18n } from '@aqx-core/common/i18n';

import { basePeriods, horizonExcludePeriods } from '../constants';
import { EPeriodGroupKey } from '../types/period';

/* eslint-disable-next-line complexity */
export const mapPeriodToText = (period: EChartPeriod, isNative?: boolean, lng?: TAppLanguageISOCountryCode) => {
  switch (period) {
    case EChartPeriod.PERIOD_M1:
      return isNative ? i18n.t('1m', { lng }) : i18n.t('1min', { lng });
    case EChartPeriod.PERIOD_M2:
      return isNative ? i18n.t('2m', { lng }) : i18n.t('2min', { lng });
    case EChartPeriod.PERIOD_M3:
      return isNative ? i18n.t('3m', { lng }) : i18n.t('3min', { lng });
    case EChartPeriod.PERIOD_M4:
      return isNative ? i18n.t('4m', { lng }) : i18n.t('4min', { lng });
    case EChartPeriod.PERIOD_M5:
      return isNative ? i18n.t('5m', { lng }) : i18n.t('5min', { lng });
    case EChartPeriod.PERIOD_M6:
      return isNative ? i18n.t('6m', { lng }) : i18n.t('6min', { lng });
    case EChartPeriod.PERIOD_M10:
      return isNative ? i18n.t('10m', { lng }) : i18n.t('10min', { lng });
    case EChartPeriod.PERIOD_M15:
      return isNative ? i18n.t('15m', { lng }) : i18n.t('15min', { lng });
    case EChartPeriod.PERIOD_M20:
      return isNative ? i18n.t('20m', { lng }) : i18n.t('20min', { lng });
    case EChartPeriod.PERIOD_M30:
      return isNative ? i18n.t('30m', { lng }) : i18n.t('30min', { lng });
    case EChartPeriod.PERIOD_H1:
      return i18n.t('1H');
    case EChartPeriod.PERIOD_H2:
      return i18n.t('2H');
    case EChartPeriod.PERIOD_H3:
      return i18n.t('3H');
    case EChartPeriod.PERIOD_H4:
      return i18n.t('4H');
    case EChartPeriod.PERIOD_H6:
      return i18n.t('6H');
    case EChartPeriod.PERIOD_D1:
      return i18n.t('1D');
    case EChartPeriod.PERIOD_W1:
      return i18n.t('1W');
    case EChartPeriod.PERIOD_MN1:
      return i18n.t('1M');
    default:
      return '';
  }
};

/* eslint-disable-next-line complexity */
export const mapPeriodToFullText = (period: EChartPeriod, lng?: TAppLanguageISOCountryCode) => {
  switch (period) {
    case EChartPeriod.PERIOD_M1:
      return i18n.t('1 minute', { lng });
    case EChartPeriod.PERIOD_M2:
      return i18n.t('2 minutes', { lng });
    case EChartPeriod.PERIOD_M3:
      return i18n.t('3 minutes', { lng });
    case EChartPeriod.PERIOD_M4:
      return i18n.t('4 minutes', { lng });
    case EChartPeriod.PERIOD_M5:
      return i18n.t('5 minutes', { lng });
    case EChartPeriod.PERIOD_M6:
      return i18n.t('6 minutes', { lng });
    case EChartPeriod.PERIOD_M10:
      return i18n.t('10 minutes', { lng });
    case EChartPeriod.PERIOD_M15:
      return i18n.t('15 minutes', { lng });
    case EChartPeriod.PERIOD_M20:
      return i18n.t('20 minutes', { lng });
    case EChartPeriod.PERIOD_M30:
      return i18n.t('30 minutes', { lng });
    case EChartPeriod.PERIOD_H1:
      return i18n.t('1 hour', { lng });
    case EChartPeriod.PERIOD_H2:
      return i18n.t('2 hours', { lng });
    case EChartPeriod.PERIOD_H3:
      return i18n.t('3 hours', { lng });
    case EChartPeriod.PERIOD_H4:
      return i18n.t('4 hours', { lng });
    case EChartPeriod.PERIOD_H6:
      return i18n.t('6 hours', { lng });
    case EChartPeriod.PERIOD_D1:
      return i18n.t('1 day', { lng });
    case EChartPeriod.PERIOD_W1:
      return i18n.t('1 week', { lng });
    case EChartPeriod.PERIOD_MN1:
      return i18n.t('1 month', { lng });
    default:
      return '';
  }
};

export const getExpandedGroup = (selectedPeriod: EChartPeriod | undefined): EPeriodGroupKey => {
  if (!selectedPeriod) {
    return EPeriodGroupKey.HOURS;
  }

  if (MINUTES_SPAN_PERIODS.includes(selectedPeriod)) {
    return EPeriodGroupKey.MINUTES;
  }
  if (HOURS_SPAN_PERIODS.includes(selectedPeriod)) {
    return EPeriodGroupKey.HOURS;
  }
  if (DAYS_SPAN_PERIODS.includes(selectedPeriod)) {
    return EPeriodGroupKey.DAYS;
  }

  return EPeriodGroupKey.HOURS;
};

export const getIntegrationBasePeriod = (integrationType: EMainProductCodeType): EChartPeriod[] => {
  switch (integrationType) {
    case EMainProductCodeType.HORIZON:
      return basePeriods.filter((period) => !horizonExcludePeriods.includes(period));
    default:
      return basePeriods;
  }
};

export const getDefaultChartPeriod = (integrationType?: EMainProductCodeType): EChartPeriod => {
  return integrationType === EMainProductCodeType.HORIZON ? EChartPeriod.PERIOD_H1 : EChartPeriod.PERIOD_H4;
};

export const periodToTestIdMap: Record<EChartPeriod, EDataTestId> = {
  [EChartPeriod.PERIOD_M1]: EDataTestId.CHART_PERIOD_OPTION_1_MINUTE,
  [EChartPeriod.PERIOD_M2]: EDataTestId.CHART_PERIOD_OPTION_2_MINUTE,
  [EChartPeriod.PERIOD_M3]: EDataTestId.CHART_PERIOD_OPTION_3_MINUTE,
  [EChartPeriod.PERIOD_M4]: EDataTestId.CHART_PERIOD_OPTION_4_MINUTE,
  [EChartPeriod.PERIOD_M5]: EDataTestId.CHART_PERIOD_OPTION_5_MINUTE,
  [EChartPeriod.PERIOD_M6]: EDataTestId.CHART_PERIOD_OPTION_6_MINUTE,
  [EChartPeriod.PERIOD_M10]: EDataTestId.CHART_PERIOD_OPTION_10_MINUTE,
  [EChartPeriod.PERIOD_M15]: EDataTestId.CHART_PERIOD_OPTION_15_MINUTE,
  [EChartPeriod.PERIOD_M20]: EDataTestId.CHART_PERIOD_OPTION_20_MINUTE,
  [EChartPeriod.PERIOD_M30]: EDataTestId.CHART_PERIOD_OPTION_30_MINUTE,
  [EChartPeriod.PERIOD_H1]: EDataTestId.CHART_PERIOD_OPTION_1_HOUR,
  [EChartPeriod.PERIOD_H2]: EDataTestId.CHART_PERIOD_OPTION_2_HOUR,
  [EChartPeriod.PERIOD_H3]: EDataTestId.CHART_PERIOD_OPTION_3_HOUR,
  [EChartPeriod.PERIOD_H4]: EDataTestId.CHART_PERIOD_OPTION_4_HOUR,
  [EChartPeriod.PERIOD_H6]: EDataTestId.CHART_PERIOD_OPTION_6_HOUR,
  [EChartPeriod.PERIOD_H8]: EDataTestId.CHART_PERIOD_OPTION_8_HOUR,
  [EChartPeriod.PERIOD_H12]: EDataTestId.CHART_PERIOD_OPTION_12_HOUR,
  [EChartPeriod.PERIOD_D1]: EDataTestId.CHART_PERIOD_OPTION_1_DAY,
  [EChartPeriod.PERIOD_W1]: EDataTestId.CHART_PERIOD_OPTION_1_WEEK,
  [EChartPeriod.PERIOD_MN1]: EDataTestId.CHART_PERIOD_OPTION_1_MONTH
};
